import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { DriveStep, PopoverDOM } from 'driver.js';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import {
    RavenService,
    type RavenService as RavenServiceType,
} from '@telecom/shared/services/raven-service/raven-service';
import { TRACK_EVENT_TYPE } from '@/constants';
import { TRACK_EVENT_AGENT_GUIDE_STEP2_SPOTLIGHTED } from '@/constants/track-events';
import { messages } from '../../locales';
import { registerOnboardingLocaleMessages } from '../../utils/onboarding-locale';
import type {
    OnboardingStepTwoAgent,
    OnboardingStepTwoGuideService,
} from '../onboarding-step-two-guide-service';

/** Step 2 无按钮气泡的公共样式类。 */
const STEP_TWO_POPOVER_CLASS = 'onboarding-step-two-guide-popover';

/** 移除 Driver 默认操作区，仅保留当前场景的说明文案。 */
function removePopoverControls(popover: PopoverDOM): void {
    popover.closeButton.remove();
    popover.footer.remove();
}

/** 统一生成 Step 2 文案气泡并记录三类 Agent 的曝光。 */
@injectable()
export class OnboardingStepTwoGuideServiceImpl implements OnboardingStepTwoGuideService {
    constructor(
        /** 提供当前语言、翻译和动态语言注册能力。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 上报成功激活的 Step 2 曝光。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
    ) {}

    /** 创建无按钮、允许目标交互的 Step 2 说明步骤。 */
    createStep(element: DriveStep['element'], hasPrefilledInput: boolean): DriveStep {
        registerOnboardingLocaleMessages(this._i18nService, messages);
        const descriptionKey = hasPrefilledInput
            ? 'onboarding.stepTwo.prefilledDescription'
            : 'onboarding.stepTwo.emptyDescription';
        return {
            element,
            disableActiveInteraction: false,
            popover: {
                description: this._i18nService.t(descriptionKey),
                side: 'bottom',
                align: 'start',
                showButtons: ['close'],
                popoverClass: STEP_TWO_POPOVER_CLASS,
                onPopoverRender: removePopoverControls,
            },
        };
    }

    /** 上报一次成功激活的 Step 2 曝光，埋点失败不影响业务引导。 */
    trackSpotlight(agent: OnboardingStepTwoAgent, hasPrefilledInput: boolean): void {
        void this._ravenService.track(TRACK_EVENT_AGENT_GUIDE_STEP2_SPOTLIGHTED, {
            eventType: TRACK_EVENT_TYPE.SS_PLG_PAGE_VIEW,
            sourceType: TRACK_EVENT_AGENT_GUIDE_STEP2_SPOTLIGHTED,
            eventDetailStr: {
                activeAgent: agent,
                hasPrefilledInput,
            },
        }).catch(() => undefined);
    }
}
